<template>
  <div class="position-relative">
    <qx-spinner
      v-if="isDataLoading || showSpinner"
      :id="`${tableId}-spinner`" />
    <!-- Actions -->
    <div
      v-show="(staticActions.length || dynamicActions.length) && !(hideActions && !collection.length)"
      ref="tableActions"
      class="actions-menu table-actions">
      <div
        ref="toolbar"
        class="toolbar">
        <section
          v-if="staticActions.length > 0"
          ref="mainActions"
          class="main-actions d-flex">
          <qx-button
            v-for="staticAction in staticActions"
            :id="staticAction.id"
            :key="staticAction.id"
            :class="staticAction.variant"
            class="mr-2"
            :disabled="!staticAction.active"
            :state="staticAction.state || 'active-blue'"
            :action="staticAction.active ? staticAction.action : null">
            <template #button-content>
              <div class="d-flex align-center">
                <em
                  :title="staticAction.title"
                  class="action-button-icon"
                  :class="staticAction.icon"
                  aria-hidden="true" />
                <span>{{ staticAction.title }}</span>
              </div>
            </template>
          </qx-button>
          <div>
            <slot name="last-static-btn" />
          </div>
        </section>
        <section class="dynamic-actions">
          <template
            v-for="dynamic in firstLevelDynamicActions"
            :key="`${dynamic.id}-dynamic`">
            <qx-button
              :id="dynamic.id"
              class="dynamic-actions-button"
              :class="dynamic.variant"
              :state="dynamic.state || 'blue-text-hover-fill'"
              :disabled="isDynamicActionDisabled(dynamic)"
              :action="() => !dynamic.inProgress && dynamic.action(selectedIds)">
              <template #button-content>
                <div class="d-flex align-center">
                  <em
                    :title="dynamic.title"
                    class="action-button-icon"
                    :class="dynamic.icon"
                    aria-hidden="true" />
                  <span>{{ dynamic.title }}</span>
                </div>
              </template>
            </qx-button>
            <qx-popover
              v-if="dynamic.id === `${tableId}-more-action`"
              custom-class="more-actions"
              location="bottom center"
              origin="auto"
              :target="`#${tableId}-more-action-button`">
              <ul class="more-actions-block">
                <div
                  v-for="(actions, index) in moreDynamicActions"
                  :key="index"
                  class="delimiter">
                  <li
                    v-for="action in actions"
                    :key="`${action.id}-more`"
                    class="d-flex align-center"
                    :class="[
                      action.id,
                      {
                        'wait-cursor': action.inProgress,
                        inactive: isDynamicActionDisabled(action),
                      },
                      action.variant,
                    ]"
                    @click="!isDynamicActionDisabled(action) && triggerActionFromMore(action, selectedIds)">
                    <em :class="action.icon" />
                    <span :title="action.title">{{ action.title }}</span>
                  </li>
                </div>
              </ul>
            </qx-popover>
          </template>
        </section>
        <section
          ref="rightToolbar"
          class="right-toolbar">
          <div class="d-flex align-center">
            <qx-selected-row-counter
              :collection-length="listedCollectionLength"
              :full-collection-length="fullCollectionLength"
              :checked-length="selectedIds.length"
              :is-filter-active="blockFiltersEnabled ? isBlockFilterApplied : isFilterActive" />

            <qx-button
              v-if="blockFiltersEnabled && blockFilters"
              id="filter-toggler"
              state="border-blue-outline"
              :action="activateBlockFilters">
              <template #button-content>
                <div class="d-flex align-center">
                  <em
                    title="Filters"
                    class="action-button-icon qx-icon icon-filter"
                    aria-hidden="true" />
                  <span>{{ string.filtersLabel }} ({{ appliedBlockFiltersAmount }})</span>
                </div>
              </template>
            </qx-button>

            <qx-filter
              v-if="!blockFiltersEnabled && filters && fullCollection.length > 0"
              :filters="filters"
              :filter-states="filterStates"
              :collection="fullCollection"
              v-bind="filterOptions"
              @onChangeFilter="onChangeFilter"
              @onClearFilters="onClearFilters"
              @onChangeTextFilter="onChangeTextFilter"
              @onEnter="onEnter" />
            <slot name="right-toolbar" />
          </div>
        </section>
      </div>
    </div>

    <!-- Additional block -->
    <div ref="additionalBlocks">
      <qx-block-filters
        v-if="blockFiltersToggled"
        v-model="blockFilterStates"
        :filters="blockFilters" />

      <slot
        class="additional-blocks"
        name="additional-block" />
    </div>
    <!-- Table -->
    <div
      class="table-block"
      tabindex="1"
      @keydown.up.prevent="moveWithArrows($event, -1)"
      @keydown.down.prevent="moveWithArrows($event, 1)"
      @keydown.enter="onDblClick(getActiveList())"
      @keydown.space="selectLine(getActiveItem())"
      @keydown.delete="removeItem"
      @keydown.esc="deselectAll">
      <div
        :id="tableId"
        ref="qTable"
        class="qTable">
        <table
          v-if="collection.length"
          ref="tableHeader"
          :style="`--scroll-bar-width: ${tableHasScroll ? `${scrollBarWidth}px` : '0px'};`"
          :aria-describedby="`${tableId}-header-caption`"
          :class="{ 'dynamic-columns': isCustomizable }"
          class="table-header">
          <tr v-if="groupedColumns && groupedColumns.length > 0">
            <th v-if="isCheckbox" />
            <th
              v-for="column in groupHeaderColumns"
              :key="column.id"
              :class="{ 'underlined-group': column.id === 'group' }"
              :style="{ width: getColumnWidth(column) }"
              :colspan="column.id === 'group' ? groupedColumns.length : '0'">
              <span v-if="column.id === 'group'">{{ column.title }}</span>
            </th>
          </tr>
          <tr class="position-relative">
            <th
              v-if="isCheckbox"
              scope="col"
              class="c-col-checkbox header-checkbox">
              <qx-checkbox
                :checked="headerCheckbox.checked"
                :disabled="headerCheckbox.disabled"
                :indeterminate="headerCheckbox.indeterminate"
                @onChange="selectAll" />
            </th>
            <th
              v-for="(column, index) in visibleColumns"
              :key="column.id"
              :style="{ width: getColumnWidth(column) }"
              :class="column.headerClasses"
              class="dynamic-columns"
              scope="col">
              <em
                v-if="index === 0 && refreshAction"
                id="header-refresh"
                class="qx-icon icon-refresh mr-2"
                @click="refresh" />
              <span
                :id="`${column.id}-column`"
                :data-sort="column.sort"
                class="col-head d-flex"
                @click="column.sort && sortColumn(column)">
                <slot :name="`column-header:${column.id}`">
                  <!-- should be in one line to avoid strange underline after title -->
                  <span :title="column.title">{{ column.title }}</span><qx-info-icon
                    v-if="column.info"
                    :id="column.info.id"
                    class="flex-shrink-0"
                    :title="column.info.title"
                    :is-html="column.info.html" />
                </slot>
                <em
                  v-if="sortParams.sortAttr && column.id === sortParams.sortAttr.id"
                  :class="{
                    'icon-arrow-up': sortParams.lastDir === 1,
                    'icon-arrow-down': sortParams.lastDir === -1,
                  }"
                  class="sort-arrow qx-icon" />
              </span>
            </th>
            <div
              v-if="isCustomizable && Object.keys(columnsForSelect).length"
              class="customize-columns">
              <em
                id="settings-trigger"
                :title="settingIconTitle"
                class="qx-icon icon-settings"
                aria-hidden="true"
                @click="togglePopover" />
              <qx-popover
                target="#settings-trigger"
                custom-class="settings-list"
                location="start bottom"
                origin="top center"
                @hidden="hidePopover">
                <div
                  tabindex="1"
                  class="select-columns-container">
                  <div class="list-title">
                    {{ settingIconTitle }}
                  </div>
                  <div
                    v-for="(column, key) in columnsForSelect"
                    :key="`select-column-${key}`"
                    class="select-column-row">
                    <qx-checkbox
                      :id="`sc-${key}`"
                      :checked="column.value"
                      :label="column.title"
                      @onChange="handleChangeColumns(key)" />
                  </div>
                  <slot name="table-settings" />
                </div>
              </qx-popover>
            </div>
          </tr>
        </table>

        <!-- Table content -->
        <div
          :id="`${tableId}-table-container`"
          ref="tableContainer"
          :style="{ height: autoHeight ? 'auto' : `${height}px` }"
          class="table-container"
          @scroll.passive="handleScroll">
          <slot
            v-if="isEmptyShown"
            name="empty-list">
            <qx-empty-filter-results v-if="fullCollection.length && !collection.length" />
          </slot>
          <div
            v-else
            ref="tableBody"
            :style="{ height: virtualScroll ? `${collectionHeight}px` : 'auto' }"
            :aria-describedby="`${tableId}-body-caption`"
            class="table-body">
            <div ref="tableRowsContainer">
              <div
                v-for="(item, index) in visibleCollection"
                :key="item.id">
                <qx-tooltip
                  v-if="item.rowTip"
                  target="parent"
                  :text="item.rowTip" />

                <tr
                  :data-id="item.id"
                  :class="{
                    disabled: item.isActive === false,
                    selected: item.isCheckedState,
                    active: allowedActiveState && item.isActiveState,
                    [item.customClass]: item.customClass !== undefined && item.customClass !== null,
                  }"
                  :style="{ height: `${rowHeight}px` }"
                  class="table-row"
                  @contextmenu.prevent="onContextMenu($event, item.id)"
                  @click="handleClickOnRow($event, item.id)">
                  <td
                    v-if="isCheckbox"
                    class="c-col-checkbox"
                    @click.prevent.stop="handleClickOnRow($event, item.id, true)">
                    <qx-checkbox
                      v-if="item.isCheckboxVisible !== false"
                      :disabled="item.isDisabledState"
                      :checked="item.isCheckedState"
                      :disabled-tip="disabledCheckboxTip"
                      :indeterminate="item.partiallySelected"
                      @onChange="onLineChecked(item.id)" />
                  </td>
                  <td
                    v-for="column in visibleColumns"
                    :key="column.id"
                    :style="{ width: getColumnWidth(column) }"
                    :class="{ 'selected-row': idsToMark && ~idsToMark.indexOf(item.id), ...column.cellClasses }"
                    @dblclick="onDblClick(item.id)">
                    <slot
                      :name="`cell:${column.id}`"
                      :item="item"
                      :index="index">
                      <span
                        :class="getClasses(column)"
                        :title="getData(column, item) && stripTags(getData(column, item))"
                        v-html="$sanitize(getData(column, item))" />
                      <copy-button
                        v-if="displayCopyButton(column.id)"
                        class="copy-button"
                        :string-to-copy="getData(column, item)" />
                    </slot>
                  </td>
                </tr>
                <slot
                  name="below-row"
                  :item="item" />
              </div>

              <div
                v-if="isOnlyPreviousDir"
                :style="{ height: `${tableHeight - rowHeight}px` }">
                <slot name="only-previous-dir" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <context-menu
      v-if="isContextMenuVisible"
      ref="contextMenu"
      :dynamic-actions="visibleDynamicActions"
      :context-event="contextEvent"
      :ids="contextMenuIds"
      @hide="hideContextMenu" />

    <span
      :id="`${tableId}-header-caption`"
      v-translate
      class="dsp-none">Table Header</span>
    <span
      :id="`${tableId}-body-caption`"
      v-translate
      class="dsp-none">Table Body</span>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import _escape from 'lodash/escape';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';

import { mapState, mapMutations } from 'vuex';

import QxTooltip from '@/elements/QxTooltip';
import QxSpinner from '@/elements/spinner.vue';
import QxCheckbox from '@/elements/form/QxCheckbox.vue';
import QxButton from '@/elements/button.vue';
import QxInfoIcon from '@/elements/infoIcon.vue';
import QxPopover from '@/elements/QxPopover.vue';
import QxFilter from '@/elements/filters.vue';
import QxBlockFilters from '@/elements/blockFilters.vue';
import QxSelectedRowCounter from '@/elements/QxSelectedRowCounter.vue';
import QxEmptyFilterResults from '@/elements/QxEmptyFilterResults.vue';
import ContextMenu from '@/elements/table/ContextMenu.vue';
import CopyButton from '@/elements/activity/CopyButton.vue';
import { isMobile, stripTags, getNonEmptyObjectProps } from '@/elements/utils.js';
import EventBus from '@/elements/eventBus.js';
import filterMixin from '@/mixins/filter.js';
import { searchFuzzyMatches } from '@/utils/filters.js';

const MIN_TABLE_HEIGHT = 360;
const TABLE_ROW_HEIGHT = 56;
const AVERAGE_ACTION_WIDTH = 145;
const COLUMNS_WITH_COPY_BUTTON = ['uid'];

export default {
  name: 'QxTableVirtualScroll',
  components: {
    QxSpinner,
    QxCheckbox,
    QxButton,
    QxInfoIcon,
    QxPopover,
    QxFilter,
    QxSelectedRowCounter,
    QxEmptyFilterResults,
    ContextMenu,
    QxBlockFilters,
    QxTooltip,
    CopyButton,
  },
  mixins: [filterMixin],
  props: {
    autoHeight: {
      type: Boolean,
      default: false,
    },
    virtualScroll: {
      type: Boolean,
      default: true,
    },
    activePagination: {
      type: Boolean,
      default: false,
    },
    scroll: {
      type: Object,
      default() {
        return {
          limit: 200,
          persentage: 20,
          allData: false,
          callInProgress: false,
        };
      },
    },
    rowHeight: {
      type: Number,
      default: TABLE_ROW_HEIGHT,
    },
    isCheckbox: {
      type: Boolean,
      default: true,
    },
    isMultiSelect: {
      type: Boolean,
      default: true,
    },
    isCustomizable: {
      type: Boolean,
      default: false,
    },
    staticActions: {
      type: Array,
      default: () => [],
    },
    dynamicActions: {
      type: Array,
      default: () => [],
    },
    allowedActions: {
      type: Object,
      default: () => ({}),
    },
    dynamicActionsCount: {
      type: Number,
      default: null,
    },
    tableId: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    columnsGroup: {
      type: Object,
      default: null,
    },
    emptyMsg: {
      type: String,
      default: '',
    },
    dblClickAction: {
      type: Function,
      default: () => {},
    },
    fullCollection: {
      type: Array,
      required: true,
    },
    filters: {
      type: Object,
      default: null,
    },
    filterOptions: {
      type: Object,
      default: null,
    },
    idsToMark: {
      type: Array,
      default: () => [],
    },
    allowedActiveState: {
      type: Boolean,
      default: true,
    },
    sortKey: {
      type: String,
      default: '',
    },
    comparator: {
      type: Function,
      default: null,
    },
    disabledCheckboxTip: {
      type: String,
      default: null,
    },
    hideActions: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
    refreshAction: {
      type: Function,
      default: null,
    },
    outsideTableHeight: {
      type: Number,
      default: null,
    },
    isDataLoading: {
      type: Boolean,
      default: false,
    },
    blockFiltersEnabled: {
      type: Boolean,
      default: false,
    },
    blockFilters: {
      type: Array,
      default: null,
    },
    manualFilterState: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'columnsLoaded',
    'changeRows',
    'deleteRows',
    'selectAll',
    'rowClick',
    'lineChecked',
    'columnSelected',
    'scrollContent',
    'renderNextPage',
  ],
  data() {
    return {
      showSpinner: false,
      startPointer: 0,
      headerCheckbox: {
        checked: false,
        disabled: false,
        indeterminate: false,
      },
      isContextMenuVisible: false,
      columnsForSelect: {},
      lastSelected: null, // last selected id for shift action,
      settingIconTitle: this.$gettext('Settings'),
      contextEvent: null,
      tableHeight: MIN_TABLE_HEIGHT,
      isPopoverOpen: false,
      tableHasScroll: false,
      scrollBarWidth: 0,
      contextMenuIds: [],
      selectedIds: [],
      isMoreOpen: false,
      isEmptyShown: false,
      firstLevelDynamicActions: [],
      blockFiltersToggled: false,
      blockFilterStates: {},
      isFuzzySort: false,
    };
  },
  computed: {
    ...mapState({
      userId: (state) => state.profile.id,
      language: (state) => state.language,
    }),
    string() {
      return {
        filtersLabel: this.$gettext('Filters'),
      };
    },

    collection() {
      if (this.blockFiltersEnabled && this.blockFilters) {
        return this.getFilteredCollectionForBlockFilters(
          this.blockFilters,
          this.blockFilterStates,
          this.fullCollection
        );
      }
      return this.filters
        ? this.handleListLoading({
            filters: this.filters,
            filterStates: this.filterStates,
            collection: this.fullCollection,
          })
        : this.fullCollection;
    },

    sortParams() {
      return (!this.isFuzzySort && this.sortKey && this.$store.state.sorting[this.sortKey]) || {};
    },
    sortedCollection() {
      if (this.isFuzzySort) {
        return this.collection;
      }
      return this.collection.slice(0).sort(this.comparator || this.sortItems);
    },
    groupedColumns() {
      return this.columnsGroup && this.visibleColumns.filter((column) => this.columnsGroup.columns.includes(column.id));
    },
    groupHeaderColumns() {
      if (this.groupedColumns) {
        const index = this.visibleColumns.findIndex((column) => column.id === this.groupedColumns[0].id);
        const visibleColumns = [...this.visibleColumns];
        const width = this.groupedColumns.reduce((result, item) => result + item.widthGrow, 0);
        visibleColumns.splice(index, this.groupedColumns.length, {
          ...this.columnsGroup,
          id: 'group',
          widthGrow: width,
        });
        return visibleColumns;
      }
      return [];
    },
    visibleColumns() {
      return this.columns.filter(({ id, isVisible = true, isCustomizable = true }) => {
        if (!this.isCustomizable || !isCustomizable || !this.columnsForSelect[id]) {
          return isVisible;
        }
        return isVisible && this.columnsForSelect[id].value;
      });
    },
    customizableColumns() {
      return this.columns.filter(({ id, isVisible = true, isCustomizable = true }) => {
        return isVisible && isCustomizable;
      });
    },
    columnsMultiplier() {
      return this.visibleColumns.reduce((muliplier, column) => {
        return muliplier + column.widthGrow;
      }, 0);
    },
    dynamicColumnWidth() {
      return `${this.visibleColumns.reduce((a, b) => {
        if (b.width) {
          return a - b.width.slice(0, b.width.length - 1);
        }
        return a;
      }, 100)}%`;
    },
    itemsCount() {
      return Math.ceil(this.tableHeight / this.rowHeight);
    },
    collectionHeight() {
      return this.collection.length * this.rowHeight;
    },
    visibleCollection() {
      if (this.virtualScroll) {
        if (this.sortedCollection) {
          return this.sortedCollection.slice(
            this.startPointer,
            Math.min(this.startPointer + this.itemsCount, this.sortedCollection.length)
          );
        } else {
          return this.visibleCollection || [];
        }
      }
      return this.sortedCollection || this.collection || [];
    },
    visibleDynamicActions() {
      if (!this.dynamicActions.length) {
        return [];
      }
      const pasteAction = [];
      const dynamicActions = this.dynamicActions.map((actions) => {
        const paste = actions.find(({ permission }) => permission === 'isPaste' && this.allowedActions[permission]);
        if (paste) {
          pasteAction.push({ ...paste, prioritized: true });
        }
        return actions.filter(
          (action) => (!paste || action.permission !== 'isPaste') && this.allowedActions[action.permission]
        );
      });
      return [pasteAction, ...dynamicActions].filter((actions) => actions.length > 0);
    },
    moreDynamicActions() {
      let result = [];
      const firstLevelIds = this.firstLevelDynamicActions.map((action) => action.id);
      this.visibleDynamicActions.forEach((actionBlock) => {
        const visible = actionBlock.filter((action) => !firstLevelIds.includes(action.id));
        if (visible.length > 0) {
          result.push(visible);
        }
      });
      return result;
    },
    // It is only for Files/AddCloudFiles/Select folder
    isOnlyPreviousDir() {
      return this.sortedCollection.length === 1 && this.sortedCollection[0].isPreviousDir;
    },
    listedCollectionLength() {
      return this.collection.filter((item) => !item.isPreviousDir).length;
    },
    fullCollectionLength() {
      return this.fullCollection.filter((item) => !item.isPreviousDir).length;
    },
    appliedBlockFiltersAmount() {
      return getNonEmptyObjectProps(this.blockFilterStates);
    },
    isBlockFilterApplied() {
      return this.appliedBlockFiltersAmount > 0;
    },
    height() {
      return this.outsideTableHeight ? this.tableHeight - this.outsideTableHeight : this.tableHeight;
    },
  },
  watch: {
    language() {
      // recalculate columns for select for correct translation
      // TODO: rewrite it to awoid change language watcher - made it computed prop
      this.defineColumnsForSelect();
    },
    columns: {
      deep: true,
      handler() {
        // to have real columns if visibility one of them depends on async action that may change
        this.defineColumnsForSelect();
      },
      immediate: true,
    },
    visibleDynamicActions: {
      deep: true,
      handler() {
        this.setFirstLevelDynamicActions();
      },
    },
    isDataLoading(val) {
      if (!val) {
        this.headerCheckbox.checked = false;
        this.headerCheckbox.indeterminate = false;
      }
    },
    'filterStates.textFilter': {
      handler(val) {
        this.isFuzzySort = val?.length > 0;
      },
      immediate: true,
    },
    manualFilterState(newFilterState) {
      const filterValue = newFilterState?.value;
      const filterState = newFilterState?.state;
      const isFilteredStateAvailable = isString(filterValue) && this.filterStates.hasOwnProperty(filterValue);
      const isProvidedStateCorrect = isBoolean(filterState) || isString(filterState);
      const isStateCanBeUpdated = isFilteredStateAvailable && isProvidedStateCorrect;

      if (isStateCanBeUpdated) {
        this.filterStates[filterValue] = filterState;
      }
    },
  },
  mounted() {
    this.getScrollWidth();
    this.fitListToScreen();
    if (this.blockFiltersEnabled) {
      this.fillBlockFilterStates(this.blockFilters);
    }
  },
  activated() {
    this.fitListToScreen();
  },
  beforeUnmount() {
    this.removeListeners();
  },
  created() {
    this.addListeners();
    EventBus.$on('upload-box-modal:show', () => {
      this.startPointer = 0;
      this.virtualScroll && this.changeListPosition({ position: 0 });
    });
    EventBus.$on('setTableSpinner', (state) => {
      this.showSpinner = state;
    });
    // Watchers are declarated here because of: https://stackoverflow.com/questions/45908853/vuejs-2-debounce-on-multiple-components
    this.$watch(
      'collection.length',
      debounce(() => {
        this.fitListToScreen();
        this.virtualScroll && this.changeListPosition({});
        // to make sure that message about empty search appears smoothly
        this.isEmptyShown = this.collection.length === 0;
      }, 100),
      { immediate: true, deep: true }
    );
    this.$watch(
      'collection',
      debounce(() => {
        this.changeHeaderCheckbox();
      }, 100),
      { immediate: true, deep: true }
    );
  },
  methods: {
    ...mapMutations({
      changeDirection: 'sorting/changeDirection',
      changeColumn: 'sorting/changeColumn',
    }),
    refresh() {
      this.refreshAction();
    },
    defineColumnsForSelect() {
      if (this.isCustomizable) {
        let savedColumns = {};
        let columnsForEmit = {};
        try {
          savedColumns = JSON.parse(window.localStorage.getItem(`user:${this.userId}:columns:${this.tableId}`)) || {};
        } catch (error) {
        } finally {
          for (let column of this.customizableColumns) {
            let value = column.showByDefault !== false;
            if (Object.hasOwn(savedColumns?.[column.id] || {}, 'value')) {
              value = savedColumns[column.id].value;
            } else if (Object.hasOwn(column || {}, 'value')) {
              value = column.value;
            }
            this.columnsForSelect[column.id] = { value, title: column.title };
            columnsForEmit[column.id] = value;
          }
          this.$emit('columnsLoaded', columnsForEmit);
        }
      }
    },
    getColumnWidth(column) {
      return (
        column.width ||
        (column.widthGrow ? `${(100 / this.columnsMultiplier) * column.widthGrow}%` : this.dynamicColumnWidth)
      );
    },
    getScrollWidth() {
      const scrollDiv = document.createElement('div');
      scrollDiv.className = 'scrollbar-measure';
      document.body.appendChild(scrollDiv);

      this.scrollBarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

      document.body.removeChild(scrollDiv);
    },
    handleClickOnRow({ shiftKey: isShift, metaKey: isMeta, ctrlKey: isCtrl }, id, isCheckbox) {
      const currentItem = this.getById(id);
      if (!currentItem || currentItem.isDisabledState) {
        return;
      }

      if (!this.isMultiSelect) {
        if (isCheckbox) {
          this.selectLine(id);
        } else {
          this.onClick(id);
        }
        return;
      }

      if (isShift) {
        this.shiftClick(id);
        return;
      }

      if (isCheckbox) {
        this.selectLine(id);
        return;
      }

      if (isMeta || isCtrl) {
        this.selectLine(id);
        this.activateLine(id);
        return;
      }
      if (!currentItem.isActiveState && this.allowedActiveState) {
        this.deselectAll();
      }
      this.onClick(id);
    },
    handleResize() {
      this.fitListToScreen();
      this.getScrollWidth();
      this.setFirstLevelDynamicActions();
    },
    addListeners() {
      if (!this.autoHeight) {
        window.addEventListener('resize', this.handleResize);
      }
      window.addEventListener('keydown', this.hideContextMenu);
      window.addEventListener('keydown', this.hideMore);
    },
    removeListeners() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('keydown', this.hideContextMenu);
      window.removeEventListener('keydown', this.hideMore);
    },
    triggerChange() {
      this.selectedIds = this.getActiveList();
      this.$emit('changeRows', this.selectedIds);
      this.$forceUpdate();
    },
    removeItem() {
      this.$emit('deleteRows', this.selectedIds);
    },
    getIndexById(id) {
      return this.sortedCollection.findIndex((model) => model.id === id);
    },
    getById(id) {
      return this.sortedCollection.find((model) => model.id === id);
    },
    selectAll() {
      const newState = !this.headerCheckbox.checked;
      this.headerCheckbox.checked = newState;
      this.headerCheckbox.indeterminate = false;
      if (this.collection.length) {
        this.collection.forEach((model) => {
          if (!model.isDisabledState) {
            model.isCheckedState = newState;
            if (model.partiallySelected) {
              model.partiallySelected = false;
            }
          }
        });

        this.lastSelected = this.collection[newState ? this.collection.length - 1 : 0].id;
        this.triggerChange();
      }
      this.$emit('selectAll', true);
    },
    deselectAll() {
      this.headerCheckbox.checked = false;
      this.collection.forEach((model) => {
        if (!model.isDisabledState) {
          model.isCheckedState = false;
          if (model.partiallySelected) {
            model.partiallySelected = false;
          }
        }
      });
      this.lastSelected = null;
      this.triggerChange();
      this.$emit('selectAll', false);
    },
    getCheckedRowsIds() {
      return this.sortedCollection
        .filter((item) => item.isCheckedState && !item.isDisabledState)
        .map((item) => item.id);
    },
    getActiveList() {
      const activeItem = this.getActiveItem();
      this.checkedRowsIds = this.getCheckedRowsIds();
      return this.checkedRowsIds.length > 0 ? this.checkedRowsIds : Boolean(activeItem) ? [activeItem] : [];
    },
    selectLine(id) {
      const model = this.collection.find((item) => item.id === id);
      if (model && !model.isDisabledState) {
        if (model.partiallySelected) {
          model.partiallySelected = false;
          model.isCheckedState = true;
        } else {
          model.isCheckedState = !model.isCheckedState;
        }

        this.lastSelected = id;
        this.changeHeaderCheckbox();
        this.triggerChange();
      }
    },
    activateLine(id) {
      if (!this.allowedActiveState || !id) {
        return;
      }
      this.collection.forEach((item) => {
        item.isActiveState = item.id === id && (!item.isDisabledState || item.isPreviousDir);
        if (item.isActiveState && !item.isCheckedState) {
          //If user move without shift we need clear selection
          this.deselectAll();
        }
      });
      this.triggerChange();
    },
    changeHeaderCheckbox() {
      const checked = this.collection.reduce(
        (total, item) => (item.isCheckedState && !item.isDisabledState ? total + 1 : total),
        0
      );
      const lengthNoDisabled = this.collection.filter((item) => !item.isDisabledState)?.length || 0;

      this.headerCheckbox.indeterminate = checked > 0 && checked < lengthNoDisabled;
      this.headerCheckbox.checked = checked === lengthNoDisabled;
    },
    onClick(id) {
      this.activateLine(id);
      if (isMobile()) {
        this.onDblClick(id);
      }
      this.$emit('rowClick', id);
    },
    onLineChecked(id) {
      this.$emit('lineChecked', id);
      return this.selectLine(id);
    },
    onDblClick(payload) {
      if (payload) {
        const list = Array.isArray(payload) ? payload : [payload];
        this.dblClickAction?.(list);
      }
    },
    onContextMenu(event, id) {
      this.hideMore();
      if (!this.dynamicActions.length) {
        return;
      }
      const model = this.collection.find((item) => item.id === id);
      if (!model.isCheckedState) {
        this.headerCheckbox.checked = false;
        this.collection.forEach((model) => {
          if (!model.isDisabledState) {
            model.isCheckedState = false;
          }
        });
      }
      this.activateLine(id);
      this.contextMenuIds = this.selectedIds;
      this.isContextMenuVisible = true;
      this.contextEvent = event;
    },
    hideContextMenu() {
      this.isContextMenuVisible = false;
      this.contextMenuIds = [];
      this.contextEvent = null;
    },
    findNextItem(currentIndex, direction) {
      const nextItem = this.sortedCollection[currentIndex + direction];

      if (!nextItem) {
        return null;
      } else if (!nextItem.isDisabledState) {
        return nextItem;
      } else {
        const directionStep = direction > 0 ? direction + 1 : direction - 1;
        return this.findNextItem(currentIndex, directionStep);
      }
    },
    moveWithShift(direction) {
      const currentItem = this.getById(this.getActiveItem());

      if (!currentItem) {
        //Neither item is selected
        const firstItem = this.sortedCollection[0];
        if (!firstItem.isDisabledState) {
          this.selectLine(firstItem.id);
        }
        return;
      }

      if (!currentItem.isCheckedState) {
        this.selectLine(currentItem.id);
      }

      const currentIndex = this.getIndexById(currentItem.id);
      const nextItem = this.findNextItem(currentIndex, direction);

      if (nextItem) {
        if (nextItem.isCheckedState) {
          /* If we selected few files in one direction and then remove some of them with opposite direction
            we need to unselect current item to avoid clear of selection
          */
          this.selectLine(currentItem.id);
        } else {
          this.selectLine(nextItem.id);
        }
        this.activateLine(nextItem.id);
        this.$refs.tableContainer.scrollTop += this.rowHeight * direction;
        // Fix for ie11
        this.$refs.qTable.focus();
      }
    },
    moveWithArrows({ shiftKey: isShift }, direction) {
      //direction equal 1 when press down arrow, and -1 when up
      if (isShift) {
        this.moveWithShift(direction);
        return;
      }

      if (!this.getActiveItem()) {
        // Neither file is in activeState
        this.activateLine(this.sortedCollection[0].id);
        return;
      }

      const currentIndex = this.getIndexById(this.getActiveItem());
      const nextItem = this.findNextItem(currentIndex, direction);

      if (nextItem) {
        this.activateLine(nextItem.id);
        this.$refs.tableContainer.scrollTop += this.rowHeight * direction;
        // Fix for ie11
        this.$refs.qTable.focus();
      }
    },
    shiftClick(id) {
      const startPointItem = this.getActiveItem() || this.lastSelected;

      if (startPointItem) {
        const lastIndex = this.getIndexById(startPointItem);
        const currentIndex = this.getIndexById(id);
        if (this.sortedCollection[currentIndex].isDisabledState) {
          return;
        }

        const from = lastIndex < currentIndex ? lastIndex : currentIndex;
        const to = lastIndex < currentIndex ? currentIndex : lastIndex;

        for (let i = from; i <= to; i++) {
          const checkedModel = this.collection.find((model) => model.id === this.sortedCollection[i].id);
          if (!checkedModel.isDisabledState) {
            checkedModel.isCheckedState = true;
          }
          if (i === currentIndex) {
            this.activateLine(checkedModel.id);
          }
        }
        this.changeHeaderCheckbox();
      } else {
        this.selectLine(id);
      }
    },
    handleChangeColumns(key) {
      this.columnsForSelect[key].value = !this.columnsForSelect[key].value;
      this.$emit('columnSelected', { key, value: this.columnsForSelect[key].value });
      this.saveColumns();
    },
    stripTags(data) {
      return stripTags(data);
    },
    getData(column, item) {
      return typeof column.format === 'function'
        ? column.format(item[column.field], item)
        : _escape(item[column.field]);
    },
    getClasses(column) {
      return column.classes ? ['row-text'].concat(column.classes).join(' ') : 'row-text';
    },
    sortItems(model1, model2) {
      const options = this.sortParams.sortAttr;
      if (!options) {
        return;
      }
      if (options.comparator) {
        // Column has custom comparator, multiply on lastDir to asc, desc sorting
        return options.comparator(model1, model2) * this.sortParams.lastDir;
      }
      let a = model1[options.attr] || '';
      let b = model2[options.attr] || '';

      if (a instanceof Array && b instanceof Array && Boolean(options.subattr)) {
        a = a.map((item) => item[options.subattr]).join(', ');
        b = b.map((item) => item[options.subattr]).join(', ');
      }

      if (a instanceof Object && b instanceof Object && Boolean(options.subattr)) {
        a = a[options.subattr];
        b = b[options.subattr];
      }

      const aLow = typeof a === 'string' ? a.toLowerCase() : a;
      const bLow = typeof b === 'string' ? b.toLowerCase() : b;

      if (aLow === bLow) {
        const subsort = options.subsort || this.sortParams.default;
        if (Boolean(subsort) && options.attr !== subsort) {
          return this.sortStrings(model1[subsort]?.toLowerCase() || '', model2[subsort]?.toLowerCase() || '', 1);
        } else {
          return 0;
        }
      }
      return aLow > bLow ? this.sortParams.lastDir : this.sortParams.lastDir * -1;
    },
    saveColumns() {
      window.localStorage.setItem(`user:${this.userId}:columns:${this.tableId}`, JSON.stringify(this.columnsForSelect));
    },
    sortStrings(a, b, col = this.sortParams.lastDir) {
      if (!a && !b) {
        return 0;
      }
      if (!a || !b) {
        return !a ? col * -1 : col;
      }

      const length = a.length;
      for (let i = 0; i <= length; i++) {
        if (a[i] > b[i]) {
          return col;
        } else if (a[i] < b[i]) {
          return col * -1;
        }
      }
      return 0;
    },
    getSortedColumn() {
      return this.columns.find((column) => column.id === this.sortParams.sortAttr.id);
    },
    getActiveItem() {
      return this.collection.find((model) => model.isActiveState)?.id || null;
    },
    sortColumn(column) {
      this.isFuzzySort = false;
      this.changeDirection(this.sortKey);
      this.changeColumn({ key: this.sortKey, column });
    },
    fitListToScreen() {
      const table = $(this.$refs.qTable);
      const modal = $(this.$refs.tableContainer).parents('.modal-body-container');
      const inModal = Boolean(modal.length);
      const tableMarginBottom = table.css('margin-bottom') ? parseInt(table.css('margin-bottom'), 10) : 0;
      const tableHeaderOuterHeight = $(this.$refs.tableHeader).outerHeight() || 0;

      if (inModal) {
        // count items that is outside the table for correct count table height
        const outsideTable = modal.find('.outside-table');
        let outsideHeight = 0;
        outsideTable.each((key, item) => (outsideHeight += $(item).outerHeight(true)));
        const tableActionsHeight = $(this.$refs.tableActions).outerHeight(true) || 0;
        const additionalBlocksHeight = $(this.$refs.additionalBlocks).outerHeight() || 0;

        this.tableHeight = Math.max(
          modal.height() -
            outsideHeight -
            tableMarginBottom -
            additionalBlocksHeight -
            tableActionsHeight -
            tableHeaderOuterHeight,
          MIN_TABLE_HEIGHT
        );

        if (this.maxHeight) {
          this.tableHeight = Math.min(this.tableHeight, this.maxHeight);
        }
      } else {
        const htmlHeight = document.documentElement.clientHeight;
        const tableOffsetTop = table.offset()?.top || 0;
        this.tableHeight = Math.abs(htmlHeight - tableOffsetTop - tableMarginBottom - tableHeaderOuterHeight);
      }
      this.tableHasScroll = this.$refs.tableContainer?.scrollHeight > this.$refs.tableContainer?.clientHeight;
    },
    togglePopover() {
      this.isPopoverOpen = !this.isPopoverOpen;
    },
    hidePopover() {
      this.isPopoverOpen = false;
    },
    toggleMore() {
      this.isMoreOpen = !this.isMoreOpen;
    },
    hideMore() {
      this.isMoreOpen = false;
    },
    triggerActionFromMore(action, selectedIds) {
      this.hideMore();
      action.action(selectedIds);
    },
    changeListPosition({ event, position }) {
      if (this.$refs.tableRowsContainer) {
        const element = event ? event.target : this.$refs.tableContainer;
        const scrollPosition = position ? position : element.scrollTop - (element.scrollTop % this.rowHeight);
        if (this.$refs.tableRowsContainer.style) {
          this.$refs.tableRowsContainer.style.transform = `translateY(${scrollPosition}px)`;
        }
        return scrollPosition;
      }
    },
    handleScroll(event) {
      if (this.virtualScroll) {
        const scrollPosition = this.changeListPosition({ event }) || 0;
        const newStart = Math.floor(event.target.scrollTop / this.rowHeight);
        this.startPointer = Math.max(
          Math.min(newStart, this.collection.length - Math.floor(this.tableHeight / this.rowHeight)),
          0
        );
        this.$emit('scrollContent', scrollPosition);
      }
      if (this.activePagination) {
        this.checkNextCall();
      }
      this.hideContextMenu();
      this.hideMore();
    },
    checkNextCall: throttle(async function () {
      await nextTick();
      const collectionHeight = this.virtualScroll ? this.collectionHeight : this.$refs.tableBody.offsetHeight;
      const topPosition = this.virtualScroll ? this.startPointer * this.rowHeight : this.$refs.tableContainer.scrollTop;
      const newCallHeight = collectionHeight - (collectionHeight * this.scroll.persentage) / 100;
      const currentBottomPosition = topPosition + this.tableHeight;
      if (!this.scroll.allData && !this.scroll.callInProgress && currentBottomPosition >= newCallHeight) {
        const { id } = this.sortedCollection.slice(-1)[0];
        this.$emit('renderNextPage', { id });
      }
    }, 100),
    scrollToChecked() {
      const firstCheckedElement = this.sortedCollection.findIndex((file) => Boolean(file.isCheckedState));
      if (firstCheckedElement !== -1) {
        this.$refs.tableContainer.scrollTop = (firstCheckedElement - this.itemsCount / 2) * this.rowHeight;
      }
    },
    async setFirstLevelDynamicActions() {
      let prioritized = [];
      const flatDA = this.visibleDynamicActions.flat();
      await nextTick();
      const visibleActionsCount = this.getFirstLevelActionsCount();
      prioritized = flatDA.filter((action) => action.prioritized);
      // all actions fit in screen - show all
      if (flatDA.length <= visibleActionsCount) {
        this.firstLevelDynamicActions = [...prioritized, ...flatDA.filter((action) => !action.prioritized)];
        return;
      }
      const more = {
        id: `${this.tableId}-more-action`,
        icon: 'qx-icon icon-more',
        permission: true,
        title: this.$gettext('More'),
        action: this.toggleMore,
      };

      // all items don't fit in screen - take only first items that fit and more
      if (prioritized.length + 1 > visibleActionsCount) {
        prioritized = prioritized.slice(0, visibleActionsCount - 1);
      }
      // not enough items - take few less valuable and more
      if (prioritized.length + 1 < visibleActionsCount) {
        const unprioritized = flatDA.filter((action) => !action.prioritized);
        prioritized = prioritized.concat(unprioritized.slice(0, visibleActionsCount - (prioritized.length + 1)));
      }
      this.firstLevelDynamicActions = [...prioritized, more];
    },
    getFirstLevelActionsCount() {
      if (this.dynamicActionsCount > 0) {
        return this.dynamicActionsCount;
      }
      const toolbarWidth = this.$refs.toolbar.clientWidth;
      const mainActionsWidth = this.$refs.mainActions?.clientWidth ?? 0;
      const rightToolbarWidth = this.$refs.rightToolbar?.clientWidth ?? 0;
      return Math.floor((toolbarWidth - mainActionsWidth - rightToolbarWidth) / AVERAGE_ACTION_WIDTH, 10);
    },
    isDynamicActionDisabled(action) {
      return action.inProgress || (Object.hasOwn(action, 'active') && !action.active);
    },
    // for filters
    fillBlockFilterStates(filters) {
      for (const filter of filters) {
        this.blockFilterStates[filter.id] = '';
      }
    },
    getFilteredCollectionForBlockFilters(filters, filterStates, fullCollection) {
      if (!this.isBlockFilterApplied) {
        return fullCollection;
      }
      this.showSpinner = true;
      let collectionToFilter = fullCollection;
      const textFilterIdx = filters.findIndex((filter) => filter.type === 'text');
      if (textFilterIdx > -1) {
        const textFilter = filters[textFilterIdx];
        collectionToFilter = searchFuzzyMatches({
          text: filterStates[textFilter.id],
          collection: collectionToFilter,
          fields: textFilter.fields,
        });
      }
      const collection = collectionToFilter.filter((item) => {
        const satisfiesFilters = filters.map((filter, index) => {
          if (textFilterIdx === index) {
            return true;
          }
          const filterState = filterStates[filter.id]?.toLowerCase()?.trim();
          if (!filterState) {
            return true;
          }
          const matchesByFields = filter.fields.map((field) => String(item[field]).toLowerCase().includes(filterState));
          return matchesByFields.some((isMatch) => isMatch);
        });
        return satisfiesFilters.every((isMatch) => isMatch);
      });
      this.showSpinner = false;
      return collection;
    },
    async activateBlockFilters() {
      this.blockFiltersToggled = !this.blockFiltersToggled;
      await nextTick();
      this.fitListToScreen();
    },
    displayCopyButton(columnId) {
      return COLUMNS_WITH_COPY_BUTTON.includes(columnId);
    },
  },
};
</script>

<style lang="scss">
.qx-multiselect-caret {
  cursor: pointer;
  font-family: 'qx-icomoon' !important;
  position: absolute;
  transform: rotate(-180deg);
  right: 4px;
  top: 5px;
  font-size: 24px;
  line-height: 24px;
  transition: transform 0.2s ease;
  &:before {
    content: '\e91b';
  }
}
.more-actions,
.settings-list {
  padding: 5px 0;
}
.scrollbar-measure {
  width: 100px;
  height: 100px;
  overflow: scroll;
  position: absolute;
  top: -9999px;
}

.table-row {
  td {
    #copy-button {
      display: none;
      margin-left: auto;
    }
    &:hover,
    &:active {
      #copy-button {
        display: inline;
      }
    }
  }
  &.active td {
    #copy-button {
      display: inline;
    }
  }
}
</style>

<style lang="scss" scoped>
.customize-columns {
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
}
.select-columns-container {
  width: fit-content;
  text-align: left;
  padding: 0 8px;
  .list-title {
    color: $grey;
    text-transform: uppercase;
    font-size: 11.5px;
    padding-bottom: 10px;
    padding-top: 3px;
  }
  .select-column-row {
    font-size: 13px;
    label {
      margin-left: 5px;
      color: $base-grey;
    }
  }
}

// files tab
.officeIcon {
  background-image: url('~@/assets/images/Microsoft-Office-logo.svg');
  background-position: center center;
  background-repeat: no-repeat;
  vertical-align: top;
  background-size: 15px;
  width: 24px;
  height: 24px;
  &::before {
    content: '';
    width: 24px;
    height: 24px;
    display: inline-block;
  }
}

// grid

.qTable {
  width: 100%;
  table-layout: fixed;
  margin-bottom: 10px;
  @include user-select(none);
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }
  .table-header {
    color: $gray-600;
    width: calc(100% - var(--scroll-bar-width));
    border-bottom: 1px solid $hover-grey;
    text-transform: uppercase;

    #header-refresh {
      cursor: pointer;
      &:hover {
        color: $base-blue;
      }
    }
    tr {
      @extend %dspFlex;
      @include align-items(center);
      th {
        span {
          position: relative;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        &.underlined-group {
          border-bottom: 1px solid $hover-grey;
        }
      }
    }
    .sort-arrow {
      text-indent: 0;
      margin-left: 2px;
      line-height: 22px;
      font-size: 14px;
    }
  }
  .table-body {
    height: 100%;
    td {
      :deep span.fa:not(.child) {
        position: relative;
      }
    }
  }
  td {
    line-height: 24px;
    @extend %ellipsisOverflow;
    cursor: pointer;
    &:empty {
      cursor: default;
    }
  }
  th {
    height: 30px;
    padding: 4px 10px;
    font: 12px/22px $regular-font;
    text-align: left;
    .txt-center {
      text-align: center !important;
    }
  }
  tr {
    td {
      height: 28px;
      padding: 2px 10px;
      font: 15px $regular-font;
      color: $base-grey;
    }
  }
  td,
  th {
    flex-grow: 1;
    display: flex;
    &.c-col-checkbox {
      width: 36px;
      &:not(.header-checkbox) {
        @extend %dspFlex;
        @include align-items(center);
      }
    }
  }
  .table-row {
    @extend %dspFlex;
    flex-direction: row;
    @include align-items(center);
    width: 100%;
    border-bottom: 1px solid $gray-200;
    td {
      height: auto;
    }
    &.active,
    &.active td {
      background-color: $b-05;
    }
    &.selected,
    &.selected td {
      background-color: $b-15 !important;
    }
    &.selected.active,
    &.selected.active td {
      background-color: $b-05 !important;
    }
    :deep .orange-links {
      margin: 0;
      float: none;
    }
    &.disabled {
      td,
      .orange-links,
      :deep .list-icons {
        color: $gray-500;
      }
    }
    &:hover,
    &:hover td {
      background: $b-05 !important;
    }
  }
  .table-container {
    overflow: auto;
  }
  .selected-row {
    font-weight: bold;
  }
}

/* Empty message */
.empty-msg {
  width: 50%;
  margin-left: 25%;
  margin-top: 15px;
  text-align: center;
  font-weight: bold;
  color: silver;
  font-size: 18px;
  display: block;
}

/* actions */
.actions-menu {
  margin-bottom: 20px;
  & > button,
  .toolbar {
    color: $base-grey;
    font-size: 24px;
    padding: 6px 0;
    height: 47px; // 35 px button height + 2 paddings * 6px
    line-height: 20px;
    z-index: 1;
    @extend %dspFlex;
    @include justify-content(flex-start);
    @include flex-direction(row);
    .dynamic-actions {
      white-space: nowrap;
      .dynamic-actions-button {
        margin-right: 8px;
      }
    }
    .main-actions {
      padding-left: 2px;
      padding-right: 1rem;
      white-space: nowrap;
      .v-btn {
        margin-right: 8px;
      }
    }
    .right-toolbar {
      @extend %dspFlex;
      @include align-items(center);
      margin-left: auto;
      margin-top: -6px;
      margin-right: 4px;
    }
    .action-button-icon {
      margin-left: -4px;
      margin-right: 4px;
      font-size: 21px;
    }
  }
}
.more-actions {
  .more-actions-block {
    list-style-type: none;
    em {
      color: $base-blue;
    }
    li {
      padding: 4px 10px;
      &:hover {
        background-color: $b-05;
      }
      &:first-child {
        margin-top: 7px;
      }
      &:last-child {
        margin-bottom: 7px;
      }
      &.inactive {
        color: $gray-400;
        cursor: auto;
        em {
          color: $gray-400;
        }
      }
      span {
        padding: 0 10px;
        cursor: pointer;
        display: inline-block;
      }
      &.danger,
      &.danger em {
        color: $red !important;
      }
    }
    .delimiter:not(:last-child) {
      border-bottom: 1px solid $gray-200;
    }
  }
}
</style>
